import { useEffect, useRef, useState } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { BiChevronDown } from 'react-icons/bi';

const SearchSelector = (props: {
    label?: string;
    hideLabel?: boolean;
    options: any;
    displayKey: string;
    onSelect: any;
    value?: string | null;
    canCreate?: boolean;
    createNew?: any;
    disabled?: boolean;
    inputSmall?: boolean;
    defaultKey?: string;
    secondaryKey?: string;
    onMenuStateChange?: (isOpen: boolean) => void;
    onSearch?: (term: string) => Promise<any[]>;
    // When set, typing a value that doesn't match any existing option shows a
    // "Créer «...»" row. Selecting it both creates the option (via this
    // callback) and selects it (via onSelect), like InputTagField's "nouveau tag".
    onCreateOption?: (value: string) => void;
}) => {
    const {
        label,
        options,
        displayKey,
        onSelect,
        hideLabel,
        value,
        canCreate,
        createNew,
        disabled,
        inputSmall = false,
        defaultKey = '_id',
        secondaryKey = false,
        onMenuStateChange,
        onSearch,
        onCreateOption,
    } = props;

    const [inputValue, setInputValue] = useState('');
    const [selected, setSelected] = useState('');
    const [open, setOpen] = useState(false);
    const [asyncResults, setAsyncResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        console.log('SearchSelector options:', options?.length || 0, 'options');
    }, [options]);

    useEffect(() => {
        if (!onSearch) return;
        if (!inputValue.trim()) {
            setAsyncResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setIsSearching(true);
            const results = await onSearch(inputValue.trim());
            setAsyncResults(results || []);
            setIsSearching(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [inputValue, onSearch]);

    useEffect(() => {
        if (onMenuStateChange) {
            onMenuStateChange(open);
        }
    }, [open, onMenuStateChange]);

    useEffect(() => {
        if (value) {
            const defaultValue = options.find(
                (o: any) => o[defaultKey] === value,
            );
            if (defaultValue !== undefined)
                setSelected(defaultValue?.[displayKey]);
        }
    }, [value, options, displayKey, defaultKey]);

    const toggleDropdown = () => {
        if (disabled) return;
        setOpen((prev) => {
            const newOpenState = !prev;

            if (newOpenState) {
                // Focus the input and reset scroll position
                if (inputRef.current) inputRef.current.focus();
                if (listRef.current) listRef.current.scrollTop = 0;
            }

            return newOpenState;
        });
    };

    return (
        <div className="h-10 w-full font-medium gap-2 relative">
            <label
                htmlFor={hideLabel ? '' : label}
                className={`
                    ${
                        inputSmall
                            ? 'text-xs font-medium'
                            : 'text-sm font-medium'
                    }
                    text-navy-700 dark:text-white`}
            >
                {hideLabel ? '' : label}
            </label>
            <div
                onClick={toggleDropdown}
                className={`${
                    inputSmall && 'h-[35px]'
                } flex w-full items-center justify-between rounded-xl border ${
                    disabled ? 'bg-gray-100' : 'bg-white bg-white/0'
                } p-3 text-sm outline-none ${
                    !selected && 'text-sm text-gray-300'
                }`}
            >
                {selected
                    ? selected?.length > 25
                        ? selected?.substring(0, 25) + '...'
                        : selected
                    : `Sélectionner`}
                <BiChevronDown
                    size={20}
                    className={`${open && 'rotate-180'}`}
                />
            </div>
            <ul
                ref={listRef}
                className={`absolute mt-2 overflow-y-auto rounded-xl bg-white shadow-lg z-[200] w-full ${
                    open ? 'max-h-60 border' : 'max-h-0'
                } `}
            >
                <div className="sticky top-0 flex items-center bg-white px-2">
                    <AiOutlineSearch size={18} className="text-gray-700" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) =>
                            setInputValue(e.target.value.toLowerCase())
                        }
                        placeholder={`Recherche...`}
                        autoFocus={false}
                        className="w-[80%] p-2 text-sm outline-none placeholder:text-gray-700"
                    />
                    {canCreate && (
                        <button
                            className="ml-10 hidden text-xs outline-none placeholder:text-gray-700 lg:block"
                            onClick={createNew}
                        >
                            Ajouter
                        </button>
                    )}
                </div>
                {onSearch ? (
                    <>
                        {isSearching && (
                            <li className="p-2 text-xs animate-pulse">Recherche...</li>
                        )}
                        {!isSearching && inputValue.trim() === '' && (
                            <li className="p-2 text-xs text-gray-400">Tapez pour rechercher...</li>
                        )}
                        {!isSearching && inputValue.trim() !== '' && asyncResults.length === 0 && (
                            <li className="p-2 text-xs text-gray-400">Aucun résultat</li>
                        )}
                        {asyncResults.map((option: any, index: number) => (
                            <li
                                key={`${option?.[displayKey]}-${index}`}
                                className={`p-2 text-xs hover:bg-gray-100 ${
                                    option?.[displayKey]?.toLowerCase() === selected?.toLowerCase()
                                        ? 'bg-gray-100'
                                        : 'bg-white'
                                }`}
                                onClick={() => {
                                    setSelected(option?.[displayKey]);
                                    onSelect(option);
                                    setOpen(false);
                                    setInputValue('');
                                    setAsyncResults([]);
                                }}
                            >
                                {option?.[displayKey]}
                                {secondaryKey && option?.[secondaryKey] && (
                                    <span className="ml-1 text-gray-400">{option[secondaryKey]}</span>
                                )}
                            </li>
                        ))}
                    </>
                ) : (
                    <>
                        {options?.map((option: any, index: number) => (
                            <li
                                key={`${option?.[displayKey]}-${index}`}
                                className={`sticky p-2 text-xs hover:bg-gray-100
                      ${
                          option?.name?.toLowerCase() === selected?.toLowerCase()
                              ? 'bg-gray-100'
                              : 'bg-white'
                      }
                      ${
                          option?.[displayKey]
                              ?.toLowerCase()
                              .includes(inputValue?.toLowerCase()) ||
                          (secondaryKey &&
                              option?.[secondaryKey]
                                  ?.toString()
                                  ?.toLowerCase()
                                  .includes(inputValue?.toLowerCase()))
                              ? 'block'
                              : 'hidden'
                      }`}
                                onClick={() => {
                                    if (
                                        option?.[displayKey]?.toLowerCase() !==
                                        selected.toLowerCase()
                                    ) {
                                        setSelected(option?.[displayKey]);
                                        onSelect(option);
                                        setOpen(false);
                                        setInputValue('');
                                    }
                                }}
                            >
                                {option?.[displayKey]}
                            </li>
                        ))}
                        {options?.length === 0 && (
                            <li className="p-2 text-xs animate-pulse">Chargement...</li>
                        )}
                        {onCreateOption &&
                            inputValue.trim() !== '' &&
                            !options?.some(
                                (o: any) =>
                                    o?.[displayKey]?.toLowerCase() ===
                                    inputValue.trim().toLowerCase(),
                            ) && (
                                <li
                                    className="flex items-center justify-between p-2 text-xs hover:bg-gray-100 cursor-pointer text-toola-600"
                                    onClick={() => {
                                        const newValue = inputValue.trim();
                                        onCreateOption(newValue);
                                        onSelect({ [defaultKey]: newValue, [displayKey]: newValue });
                                        setSelected(newValue);
                                        setOpen(false);
                                        setInputValue('');
                                    }}
                                >
                                    Créer « {inputValue.trim()} »
                                </li>
                            )}
                    </>
                )}
            </ul>
        </div>
    );
};

export default SearchSelector;
